/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { mapValues } from 'lodash';

import { LazyPermission, LazyPermissionSet, Permission, PermissionAction } from './permission';
import { addPermissionRoot } from './permission-registry';

export type PermissionInstanceLevel = Hash<Permission>;
export type PermissionLevel3 = Hash<PermissionInstanceLevel> | PermissionInstanceLevel;
export type PermissionLevel2 = Hash<PermissionLevel3> | PermissionInstanceLevel;
export type PermissionLevel1 = Hash<PermissionLevel2> | PermissionInstanceLevel;

export type PermissionLevel = PermissionLevel1 | PermissionLevel2 | PermissionLevel3;

interface PermissionLevelFactory {
  name: string;

  create(): PermissionLevel | PermissionInstanceLevel;
}

export function permission(action: PermissionAction): Permission {
  return LazyPermission.createLazy(action);
}

export function permissionSet(action: PermissionAction, permissions: Permission[]): Permission {
  return LazyPermissionSet.createLazy(action, permissions);
}

export function level<T>(name: string, nodeFactory: () => T): T {
  return {
    name,
    create: nodeFactory,
  } as any;
}

export function declare<T>(root: T): T {
  const rootLevel: any = {};

  defineLevelPath(rootLevel, []);
  defineLevelProperties(rootLevel, root);

  addPermissionRoot(rootLevel);

  return rootLevel;
}

function defineLevelProperties<T>(target: any, levelDef: T): T {
  const parentPath = target['$path'];
  const cached = {};

  return Object.defineProperties(target,
                                 <any>mapValues(<any>levelDef, (levelDefEntry: PermissionLevelFactory, key: string) =>
      ({
        configurable: false,
        enumerable: false,
        get: () => {
          let levelNode = cached[key];

          if (levelNode) {
            return levelNode;
          }

          if (levelDefEntry instanceof LazyPermission || levelDefEntry instanceof LazyPermissionSet) {
            levelNode = levelDefEntry.complete(parentPath);
          } else {
            const childPath = parentPath.concat([levelDefEntry.name]);

            const nextLevelDef = levelDefEntry.create();

            levelNode = {};
            defineLevelPath(levelNode, childPath);
            defineLevelProperties(levelNode, nextLevelDef);
          }

          cached[key] = levelNode;

          return levelNode;
        },
      })));
}

function defineLevelPath<T extends PermissionLevel | PermissionInstanceLevel>(target: any, path: string[]): void {
  Object.defineProperty(target, '$path', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: path,
  });
}
